import { EditOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import dompurify from 'dompurify';
import { get, toNumber } from 'lodash';
import { Component } from 'react';
import { withApollo } from 'react-apollo';
import Spinner from 'src/_shared/components/spinner/SpinnerComponent.jsx';
import { USER_ROLES } from '../_shared/constants';
import { queryCustomPageByCompanyIdIndex } from '../_shared/api/graphql/custom/custom-page';
import { customPageWrapper } from './customPageStyles.js';
import { Link } from 'react-router-dom';

const ALLOWED_IFRAME_SOURCES = [
	'https://www.youtube.com',
	'https://www.youtube-nocookie.com',
	'https://fast.wistia.net',
];

function sanitizeWithIframes(html) {
	const purify = dompurify;

	purify.addHook('uponSanitizeElement', (node, data) => {
		if (data.tagName === 'iframe') {
			const src = node.getAttribute('src');
			if (!src) {
				node.remove();
				return;
			}

			let isAllowed = false;
			try {
				const srcOrigin = new URL(src).origin;
				isAllowed = ALLOWED_IFRAME_SOURCES.includes(srcOrigin);
			} catch {
				isAllowed = false;
			}

			if (!isAllowed) {
				node.remove();
			}
		}
	});

	try {
		return purify.sanitize(html, {
			ADD_TAGS: ['iframe'],
			ADD_ATTR: [
				'src',
				'width',
				'height',
				'frameborder',
				'allowfullscreen',
				'allow',
				'target',
				'rel',
			],
		});
	} finally {
		purify.removeHook('uponSanitizeElement');
	}
}

class CustomPageComponent extends Component {
	constructor(props) {
		super(props);
		this.state = {
			customPageData: [],
			htmlData: '',
			doesNotExist: false,
			pageTitle: '',
		};
	}

	async componentDidMount() {
		const { customPageData = [] } = this.props;
		const pageHtmlData = customPageData.find((element) => {
			return (
				(element.pageNumber ? element.pageNumber : 1) ===
				toNumber(this.props.match.params.customPageId)
			);
		});
		if (pageHtmlData && !pageHtmlData.active) {
			this.props.history.replace('/');
			return;
		}
		this.setState({
			customPageData,
			htmlData: pageHtmlData?.content || '',
			pageTitle: pageHtmlData?.title,
			doesNotExist: pageHtmlData === undefined,
		});
	}

	async componentDidUpdate(prevProps) {
		const { customPageData = [] } = this.props;
		const { customPageId } = this.props.match.params;

		if (
			customPageData.length !== prevProps.customPageData?.length ||
			prevProps.match.params.customPageId != customPageId
		) {
			const pageHtmlData = customPageData.find((element) => {
				return (
					(element.pageNumber ? element.pageNumber : 1) ===
					toNumber(this.props.match.params.customPageId)
				);
			});
			if (pageHtmlData && !pageHtmlData.active) {
				this.props.history.replace('/');
				return;
			}
			this.setState({
				customPageData,
				htmlData: pageHtmlData?.content || '',
				pageTitle: pageHtmlData?.title,
				doesNotExist: pageHtmlData === undefined,
			});
		}
	}

	async getCustomPageData(policy = 'network-only') {
		const { client, currentUser } = this.props;
		try {
			const { data } = await client.query({
				query: queryCustomPageByCompanyIdIndex,
				variables: {
					companyId: get(currentUser, 'companyId'),
				},
				fetchPolicy: policy,
			});
			const customPageData = get(
				data,
				'queryCustomPageByCompanyIdIndex.items',
				[]
			);
			return customPageData;
		} catch (error) {
			console.log(error);
			return [];
		}
	}

	htmlDecode = (input) => {
		let returnValue = input.replaceAll('&gt;', '>');
		returnValue = returnValue.replaceAll('&lt;', '<');
		returnValue = returnValue.replaceAll('&quot;', '"');
		returnValue = returnValue.replaceAll('&apos;', "'");
		returnValue = returnValue.replaceAll('&amp;', '&');
		return returnValue;
	};

	render() {
		const { htmlData, doesNotExist, pageTitle } = this.state;
		const { currentUser, match } = this.props;
		const pageNumber = match?.params?.customPageId;

		const isAdmin =
			currentUser.displayAs === USER_ROLES.ADMIN ||
			currentUser.role === USER_ROLES.SUPER_ADMIN;

		const customPageTitle = pageTitle || `Custom Page ${pageNumber}`;

		if (!currentUser?.company) return <Spinner />;
		if (doesNotExist) return null;

		return (
			<main>
				<div className="page-title">
					<h2 className="page-heading">{customPageTitle}</h2>
					{isAdmin && (
						<ul className="info-action">
							<li>
								<Link to={`/custom-page-builder?pageNumber=${pageNumber}`}>
									<Button type="primary" icon={<EditOutlined />} size="large">
										Edit
									</Button>
								</Link>
							</li>
						</ul>
					)}
				</div>
				<div className={customPageWrapper}>
					{htmlData && (
						<div
							dangerouslySetInnerHTML={{
								__html: sanitizeWithIframes(this.htmlDecode(htmlData)),
							}}
							className="custom-page-wrapper"
						/>
					)}
				</div>
			</main>
		);
	}
}

export default withApollo(CustomPageComponent);
